import { REACT_APP_BACKEND_BASE_URL } from "configs/config";
import { BaseApiService } from "services/BaseApiService";

import { CHARITY_API_BASE_PATH, CHARITY_ADMIN_API_BASE_PATH } from "../constants";
import {
  AdminQueueResponse,
  AdminReportActionRequest,
  AdminReportActionResponse,
  AdminSubmissionStatus,
  AuthRequestResponse,
  CreateHouseholdResponse,
  CampaignSnapshot,
  CharityHousehold,
  CharityRideReport,
  EvidenceUploadResponse,
  HouseholdDeletionSummary,
  CharityRider,
  MileageDisposition,
  HouseholdLoginGrantResponse,
  HouseholdResponse,
  LatestRide,
  LatestRidesResponse,
  LeaderboardAdult,
  LeaderboardAdultApiEntry,
  LeaderboardApiResponse,
  LeaderboardData,
  LeaderboardHousehold,
  LeaderboardHouseholdApiEntry,
  LeaderboardMinor,
  LeaderboardMinorApiEntry,
  ReportsListResponse,
  RiderType,
  SubmissionResponse,
} from "../types";
import { CharitySignInDestination } from "../types/CharitySignInDestinationEnum";
import { IBatchRowCheck } from "../types/IBatchRowCheck";
import { IBatchRideRow, IBatchSubmissionResult } from "../types/IBatchSubmission";
import { IEvidenceJobView } from "../types/IEvidenceJobView";
import { IEvidencePassView } from "../types/IEvidencePass";
import { IImportCandidate } from "../types/IImportCandidate";
import { IImportOutcome } from "../types/IImportOutcome";
import { IListSubmissionResult } from "../types/IListScreenshotReading";
import { IMagicLinkApproval } from "../types/IMagicLinkApproval";
import { IPendingSignInClaim } from "../types/IPendingSignInClaim";
import { ISubmittedListRow } from "../types/ISubmittedListRow";
import { ITrackerProviderStatus } from "../types/ITrackerProviderStatus";
import { TrackerProvider } from "../types/TrackerProviderEnum";

/**
 * API service for Ride to Rebuild charity participant endpoints.
 * Handles household management, authentication, reporting, and leaderboard queries.
 * Magic-link authentication uses bearer tokens, not Auth0.
 */
export class CharityApiService extends BaseApiService {
  /**
   * Create a new household and enroll the first member.
   * Rate limited. Always returns 202 whether or not email is registered.
   *
   * @param headEmail - Email of household head
   * @param displayName - Household display name
   * @param firstRiderName - First member name
   * @param acceptedTermsVersion - Terms version the join form displayed and the user ticked
   * @param intendedDestination - Where the sign-in link should land them, when they arrived heading somewhere
   * @param pendingSignInId - A request this window is already waiting on, so a resend keeps it
   * @param agreesToPhotoSharing - Whether the photograph box was ticked. Last, so adding it shifts no existing argument
   * @returns Promise resolving to 202 response
   */
  static async createHouseholdAsync(
    headEmail: string,
    displayName: string,
    firstRiderName: string,
    acceptedTermsVersion: string,
    intendedDestination?: CharitySignInDestination,
    pendingSignInId?: string,
    inviteToken?: string,
    agreesToPhotoSharing = false
  ): Promise<CreateHouseholdResponse> {
    return this.fetchWithAuthAsync<CreateHouseholdResponse>(`${CHARITY_API_BASE_PATH}/households`, "", {
      method: "POST",
      body: JSON.stringify({
        headEmail: headEmail,
        displayName: displayName,
        firstRiderName: firstRiderName,
        acceptedTermsVersion: acceptedTermsVersion,
        agreesToPhotoSharing: agreesToPhotoSharing,
        intendedDestination: intendedDestination,
        pendingSignInId: pendingSignInId,
        inviteToken: inviteToken,
      }),
    });
  }

  /**
   * Request a magic link sign-in. Always returns 202 whether or not email is known.
   *
   * @param headEmail - Email to send sign-in link to
   * @param intendedDestination - Where the sign-in link should land them, when they arrived heading somewhere
   * @param pendingSignInId - A request this window is already waiting on, so a resend keeps it
   * @returns Promise resolving to 202 response
   */
  static async requestAuthLinkAsync(
    headEmail: string,
    intendedDestination?: CharitySignInDestination,
    pendingSignInId?: string
  ): Promise<AuthRequestResponse> {
    return this.fetchWithAuthAsync<AuthRequestResponse>(`${CHARITY_API_BASE_PATH}/auth/request`, "", {
      method: "POST",
      body: JSON.stringify({
        headEmail: headEmail,
        intendedDestination: intendedDestination,
        pendingSignInId: pendingSignInId,
      }),
    });
  }

  /**
   * Confirm a sign-in link, signing this browser in.
   *
   * Hands back a session for the caller. The window that asked for the link
   * collects a separate one of its own by polling.
   *
   * @param token - Magic link token from email
   * @returns Promise resolving to the session, or why the link was refused
   */
  static async approveAuthLinkAsync(token: string): Promise<IMagicLinkApproval> {
    return this.fetchWithAuthAsync<IMagicLinkApproval>(`${CHARITY_API_BASE_PATH}/auth/verify`, "", {
      method: "POST",
      body: JSON.stringify({ token: token }),
    });
  }

  /**
   * Ask whether this window's link has been confirmed yet.
   *
   * @param pendingSignInToken - The credential this window was given when it asked
   * @returns Promise resolving to the session, or the status explaining why there is not one
   */
  static async pollPendingSignInAsync(pendingSignInToken: string): Promise<IPendingSignInClaim> {
    return this.fetchWithAuthAsync<IPendingSignInClaim>(`${CHARITY_API_BASE_PATH}/auth/pending`, "", {
      method: "POST",
      body: JSON.stringify({ pendingSignInToken: pendingSignInToken }),
    });
  }

  /**
   * Get campaign totals and progress.
   * Cached 60s. May return 503 if no campaign is configured.
   *
   * @returns Promise resolving to campaign snapshot
   * @throws BackendError if campaign is not configured
   */
  static async getCampaignTotalsAsync(): Promise<CampaignSnapshot> {
    return this.fetchWithAuthAsync<CampaignSnapshot>(`${CHARITY_API_BASE_PATH}/totals`, "");
  }

  /**
   * Get leaderboards for households, adults, and minors.
   * Every rider taking part. Cached 60s.
   *
   * @returns Promise resolving to leaderboard data
   * @throws BackendError if no campaign is configured
   */
  static async getLeaderboardAsync(): Promise<LeaderboardData> {
    const response = await this.fetchWithAuthAsync<LeaderboardApiResponse>(`${CHARITY_API_BASE_PATH}/leaderboard`, "");

    return {
      households: this.mapLeaderboardHouseholds({ entries: response.households }),
      adults: this.mapLeaderboardAdults({ entries: response.adults }),
      minors: this.mapLeaderboardMinors({ entries: response.minors }),
    };
  }

  /**
   * Get the most recent rides, newest arrival first.
   *
   * Ordered on when a ride reached the campaign rather than on the day it was
   * ridden, so an imported ride appears as it lands. Cached 60s.
   *
   * @returns Promise resolving to the latest rides
   * @throws BackendError if no campaign is configured
   */
  static async getLatestRidesAsync(): Promise<LatestRide[]> {
    const response = await this.fetchWithAuthAsync<LatestRidesResponse>(
      `${CHARITY_API_BASE_PATH}/latest-rides`,
      ""
    );

    return response.rides;
  }

  /**
   * Get the household and its riders for the signed-in session.
   *
   * @param sessionToken - Bearer token from session
   * @returns Promise resolving to household and riders
   */
  static async getHouseholdAsync(sessionToken: string): Promise<HouseholdResponse> {
    return this.fetchWithAuthAsync<HouseholdResponse>(`${CHARITY_API_BASE_PATH}/household`, sessionToken);
  }

  /**
   * Rename the signed-in household.
   *
   * Head only — the API refuses it for anyone else. The household comes back as
   * the write left it rather than being assumed from what was sent, because the
   * name is trimmed server-side before it reaches the public board.
   *
   * @param sessionToken - Bearer token from session
   * @param displayName - The name the household should go by
   * @returns Promise resolving to the household as it now reads
   */
  static async updateHouseholdAsync(sessionToken: string, displayName: string): Promise<CharityHousehold> {
    return this.fetchWithAuthAsync<CharityHousehold>(`${CHARITY_API_BASE_PATH}/household`, sessionToken, {
      method: "PATCH",
      body: JSON.stringify({ displayName: displayName }),
    });
  }

  /**
   * Give an adult in this household a sign-in of their own.
   *
   * Head only — the API refuses it for anyone else. The grant takes effect at
   * once: there is nothing for the invited address to accept, because asking
   * for a sign-in link simply starts working.
   *
   * @param sessionToken - Bearer token from session
   * @param riderId - The adult member this address will report as
   * @param email - The address being given a sign-in
   * @returns Promise resolving to the new entry and whether the note reached them
   */
  static async grantHouseholdLoginAsync(
    sessionToken: string,
    riderId: string,
    email: string
  ): Promise<HouseholdLoginGrantResponse> {
    return this.fetchWithAuthAsync<HouseholdLoginGrantResponse>(
      `${CHARITY_API_BASE_PATH}/household/logins`,
      sessionToken,
      {
        method: "POST",
        body: JSON.stringify({ riderId: riderId, email: email }),
      }
    );
  }

  /**
   * Take an adult's sign-in away, signing their devices out with it.
   *
   * Head only. The member stays and so do their miles — losing a sign-in is
   * not the same as leaving the household.
   *
   * @param sessionToken - Bearer token from session
   * @param loginId - The sign-in being removed
   * @returns Promise resolving to what happened, and what to tell the head
   */
  static async revokeHouseholdLoginAsync(
    sessionToken: string,
    loginId: string
  ): Promise<{ outcome: string; message: string }> {
    return this.fetchWithAuthAsync<{ outcome: string; message: string }>(
      `${CHARITY_API_BASE_PATH}/household/logins/${loginId}`,
      sessionToken,
      { method: "DELETE" }
    );
  }

  /**
   * Add a new member to the household.
   *
   * @param sessionToken - Bearer token from session
   * @param displayName - Member display name (first name + last initial)
   * @param riderType - Whether the member is an adult or a minor
   * @param acceptedTermsVersion - Terms version the form displayed and the user ticked
   * @returns Promise resolving to created rider
   */
  static async addHouseholdMemberAsync(
    sessionToken: string,
    displayName: string,
    riderType: RiderType,
    acceptedTermsVersion: string
  ): Promise<CharityRider> {
    return this.fetchWithAuthAsync<CharityRider>(`${CHARITY_API_BASE_PATH}/household/members`, sessionToken, {
      method: "POST",
      body: JSON.stringify({
        displayName: displayName,
        riderType: riderType,
        acceptedTermsVersion: acceptedTermsVersion,
      }),
    });
  }

  /**
   * Rename a household member.
   * Rider type cannot be changed after creation.
   *
   * @param sessionToken - Bearer token from session
   * @param riderId - ID of rider to update
   * @param displayName - New display name (optional)
   * @returns Promise resolving to updated rider
   */
  static async updateHouseholdMemberAsync(
    sessionToken: string,
    riderId: string,
    displayName?: string
  ): Promise<CharityRider> {
    return this.fetchWithAuthAsync<CharityRider>(
      `${CHARITY_API_BASE_PATH}/household/members/${riderId}`,
      sessionToken,
      {
        method: "PATCH",
        body: JSON.stringify({
          ...(displayName !== undefined && { displayName: displayName }),
        }),
      }
    );
  }

  /**
   * Delete or hide a household member.
   * Only deleted if no reports. Otherwise hidden from rider picker.
   *
   * @param sessionToken - Bearer token from session
   * @param riderId - ID of rider to remove
   * @returns Promise resolving to outcome (deleted or hidden)
   */
  static async deleteHouseholdMemberAsync(
    sessionToken: string,
    riderId: string
  ): Promise<{ outcome: "deleted" | "hidden"; message: string }> {
    return this.fetchWithAuthAsync<{ outcome: "deleted" | "hidden"; message: string }>(
      `${CHARITY_API_BASE_PATH}/household/members/${riderId}`,
      sessionToken,
      { method: "DELETE" }
    );
  }

  /**
   * Erase the signed-in household and everything that identifies it.
   *
   * The disposition is required rather than defaulted: leaving the miles in the
   * campaign total and taking them out are both irreversible, and the privacy
   * notice promises the participant the choice between them.
   */
  static async deleteHouseholdAsync(
    sessionToken: string,
    mileageDisposition: MileageDisposition
  ): Promise<HouseholdDeletionSummary> {
    return this.fetchWithAuthAsync<HouseholdDeletionSummary>(`${CHARITY_API_BASE_PATH}/household`, sessionToken, {
      method: "DELETE",
      body: JSON.stringify({ mileageDisposition: mileageDisposition }),
    });
  }

  /**
   * Submit a ride report for one or more riders.
   * Creates one report per rider sharing a submission group.
   *
   * @param sessionToken - Bearer token from session
   * @param riderIds - Array of rider IDs reporting this ride
   * @param miles - Total miles for this ride
   * @param rideDateMs - Ride date in milliseconds
   * @param participantNote - Optional note from participant
   * @returns Promise resolving to submission with all reports created
   */
  static async submitReportAsync(
    sessionToken: string,
    riderIds: string[],
    miles: number,
    rideDateMs: number,
    participantNote?: string,
    evidenceJobId?: string
  ): Promise<SubmissionResponse> {
    return this.fetchWithAuthAsync<SubmissionResponse>(`${CHARITY_API_BASE_PATH}/reports`, sessionToken, {
      method: "POST",
      body: JSON.stringify({
        riderIds: riderIds,
        miles: miles,
        rideDateMs: rideDateMs,
        ...(participantNote && { participantNote: participantNote }),
        ...(evidenceJobId && { evidenceJobId: evidenceJobId }),
      }),
    });
  }

  /**
   * Send one ride screenshot to be read.
   *
   * Answers as soon as the image is accepted, before it has been read: the
   * reading is followed by polling getEvidenceJobAsync. Deliberately raw fetch
   * rather than the JSON helper, because the browser has to set the multipart
   * boundary itself and a forced Content-Type breaks the upload.
   *
   * @param sessionToken - Bearer token from session
   * @param file - The screenshot the participant chose
   * @returns The accepted job to poll
   * @throws {AuthenticationError} When the ride session has run out
   * @throws {BackendError} When the upload is refused, carrying the refusal reason and the server's own sentence
   */
  static async uploadEvidenceAsync(sessionToken: string, file: File): Promise<EvidenceUploadResponse> {
    const form = new FormData();
    form.append("screenshot", file);

    const response = await fetch(`${this.getBackendBaseUrl()}${CHARITY_API_BASE_PATH}/evidence`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      body: form,
    });

    if (!response.ok) {
      await this.throwForFailedResponseAsync({ response: response });
    }

    return response.json();
  }

  /**
   * Ask how a screenshot reading is getting on.
   *
   * Carries `listReading` when the screen turned out to be an activity list,
   * settled against what the household already holds at the moment of the read.
   *
   * @param sessionToken - Bearer token from session
   * @param jobId - The job returned by uploadEvidenceAsync
   * @returns The reading so far
   */
  static async getEvidenceJobAsync(sessionToken: string, jobId: string): Promise<IEvidenceJobView> {
    return this.fetchWithAuthAsync<IEvidenceJobView>(`${CHARITY_API_BASE_PATH}/evidence/${jobId}`, sessionToken);
  }

  /**
   * Send several ride screenshots to be read as one pass.
   *
   * Answers as soon as every row exists, before any of them has been read, so
   * the screen draws the rows and fills them in as the readings land. Raw fetch
   * for the same reason the single upload uses one: the browser has to set the
   * multipart boundary itself.
   *
   * @param sessionToken - Bearer token from session
   * @param files - The pictures the participant chose, in the order they chose them
   * @returns The pass, one row per picture
   * @throws {AuthenticationError} When the ride session has run out
   * @throws {BackendError} When the pass is turned away, carrying the reason and the server's own sentence
   */
  static async uploadEvidencePassAsync(sessionToken: string, files: File[]): Promise<IEvidencePassView> {
    const form = new FormData();
    files.forEach((file) => form.append("screenshots", file));

    const response = await fetch(`${this.getBackendBaseUrl()}${CHARITY_API_BASE_PATH}/evidence/pass`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${sessionToken}`,
      },
      body: form,
    });

    if (!response.ok) {
      await this.throwForFailedResponseAsync({ response: response });
    }

    return response.json();
  }

  /**
   * Ask how a pass of screenshots is getting on.
   *
   * Answers with every row each time rather than only the ones that changed: a
   * rider watching four rides arrive should not have to work out which of four
   * requests told them about which row.
   *
   * @param sessionToken - Bearer token from session
   * @param passId - The pass returned by uploadEvidencePassAsync
   * @returns The pass as it now stands
   */
  static async getEvidencePassAsync(sessionToken: string, passId: string): Promise<IEvidencePassView> {
    return this.fetchWithAuthAsync<IEvidencePassView>(
      `${CHARITY_API_BASE_PATH}/evidence/pass/${passId}`,
      sessionToken
    );
  }

  /**
   * Record the rides an activity list showed.
   *
   * Each row says what the household made of one line of the screen. A row sent
   * as read carries the reading's own figures, which the campaign checks against
   * the picture it read; a row sent as typed carries the rider's, which the
   * campaign takes on their word. A line left out of the rows is a line the
   * household dropped.
   *
   * Output shape: `{ rows: [{ rowIndex, miles, rideDateMs, refusal?, rejection?,
   * submissionGroupId? }], reports: [...], sessionsBeyondCap }`.
   *
   * @param sessionToken - Bearer token from session
   * @param jobId - The reading of the list screenshot
   * @param riderIds - Who rode. The same riders are recorded against every ride on the screen.
   * @param rows - The sessions still on the report, in the screen's own order
   * @returns What each session on the screen did
   */
  static async submitListRidesAsync(
    sessionToken: string,
    jobId: string,
    riderIds: string[],
    rows: ISubmittedListRow[]
  ): Promise<IListSubmissionResult> {
    return this.fetchWithAuthAsync<IListSubmissionResult>(
      `${CHARITY_API_BASE_PATH}/evidence/${jobId}/rides`,
      sessionToken,
      {
        method: "POST",
        body: JSON.stringify({ riderIds: riderIds, rows: rows }),
      }
    );
  }

  /**
   * Draw the rows a household is about to report, writing nothing.
   *
   * What comes back is advisory: the submit endpoint judges every row again
   * against freshly read rides, because the screen this answers may be minutes
   * old by the time the button is pressed. What it buys is a repeat named on the
   * row before it is recorded rather than only explained afterwards.
   *
   * @param sessionToken - Bearer token from session
   * @param riderIds - Who rode, for the whole batch
   * @param rows - The rides as the screen holds them
   * @returns One check per row, in the order they were sent
   */
  static async checkRideBatchAsync(
    sessionToken: string,
    riderIds: string[],
    rows: IBatchRideRow[]
  ): Promise<IBatchRowCheck[]> {
    const response = await this.fetchWithAuthAsync<{ rows: IBatchRowCheck[] }>(
      `${CHARITY_API_BASE_PATH}/reports/batch/check`,
      sessionToken,
      {
        method: "POST",
        body: JSON.stringify({ riderIds: riderIds, rows: rows }),
      }
    );
    return Array.isArray(response?.rows) ? response.rows : [];
  }

  /**
   * Record several rides in one pass.
   *
   * Answers with what landed and what did not, row by row. Partial success is
   * the ordinary outcome — three rides recorded and one explained is a good
   * result — so a refusal on one row never says anything about the rest.
   *
   * Output shape: `{ rows: [{ rowIndex, rideDateMs, miles, submissionGroupId?,
   * rejection?, flaggedAsRepeat? }], reports: [...] }`.
   *
   * @param sessionToken - Bearer token from session
   * @param riderIds - Who rode. The same riders are recorded against every row.
   * @param rows - The rides, each with its own day and distance
   * @returns What each row did and every record written
   */
  static async submitRideBatchAsync(
    sessionToken: string,
    riderIds: string[],
    rows: IBatchRideRow[]
  ): Promise<IBatchSubmissionResult> {
    return this.fetchWithAuthAsync<IBatchSubmissionResult>(`${CHARITY_API_BASE_PATH}/reports/batch`, sessionToken, {
      method: "POST",
      body: JSON.stringify({ riderIds: riderIds, rows: rows }),
    });
  }

  /**
   * Get all reports for the household.
   *
   * @param sessionToken - Bearer token from session
   * @returns Promise resolving to list of reports
   */
  static async getReportsAsync(sessionToken: string): Promise<ReportsListResponse> {
    return this.fetchWithAuthAsync<ReportsListResponse>(`${CHARITY_API_BASE_PATH}/reports`, sessionToken);
  }

  /**
   * Withdraw or correct a report while it is pending.
   *
   * @param sessionToken - Bearer token from session
   * @param reportId - ID of report to modify
   * @param action - "withdraw" or "correct"
   * @param correctedMiles - Updated miles (required if action is "correct")
   * @returns Promise resolving to updated report
   */
  static async updateReportAsync(
    sessionToken: string,
    reportId: string,
    action: "withdraw" | "correct",
    correctedMiles?: number
  ): Promise<CharityRideReport> {
    return this.fetchWithAuthAsync<CharityRideReport>(`${CHARITY_API_BASE_PATH}/reports/${reportId}`, sessionToken, {
      method: "PATCH",
      body: JSON.stringify({
        action: action,
        ...(correctedMiles !== undefined && { correctedMiles: correctedMiles }),
      }),
    });
  }

  /**
   * Get admin review queue with optional status filter.
   * Auth0-gated (organizers and co-hosts only).
   * May return 503 if campaign is not configured.
   *
   * @param authToken - Auth0 access token
   * @param status - Optional filter: "pending" | "counted" | "rejected" | "duplicate" | "withdrawn"
   * @returns Promise resolving to submissions in queue
   * @throws BackendError if campaign is not configured or user is not authorized
   */
  static async getAdminQueueAsync(authToken: string, status?: AdminSubmissionStatus): Promise<AdminQueueResponse> {
    const params = new URLSearchParams();
    if (status) {
      params.append("status", status);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return this.fetchWithAuthAsync<AdminQueueResponse>(`${CHARITY_ADMIN_API_BASE_PATH}/queue${query}`, authToken);
  }

  /**
   * Update submission status or add reviewer note.
   * Auth0-gated (organizers only).
   *
   * @param authToken - Auth0 access token
   * @param submissionGroupId - ID of submission to act on
   * @param request - Action and optional reviewer note
   * @returns Promise resolving to action confirmation
   * @throws BackendError if submission not found or user is not authorized
   */
  static async updateAdminSubmissionAsync(
    authToken: string,
    submissionGroupId: string,
    request: AdminReportActionRequest
  ): Promise<AdminReportActionResponse> {
    return this.fetchWithAuthAsync<AdminReportActionResponse>(
      `${CHARITY_ADMIN_API_BASE_PATH}/reports/${submissionGroupId}`,
      authToken,
      {
        method: "PATCH",
        body: JSON.stringify(request),
      }
    );
  }

  /**
   * Download closeout reconciliation export as CSV.
   * Auth0-gated (organizers and co-hosts only).
   *
   * @param authToken - Auth0 access token
   * @returns Promise resolving to file blob with CSV content
   * @throws BackendError if not authorized or export generation fails
   */
  static async downloadAdminExportAsync(authToken: string): Promise<Blob> {
    const response = await fetch(`${this.getBackendBaseUrl()}${CHARITY_ADMIN_API_BASE_PATH}/export`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      await this.throwForFailedResponseAsync({ response: response });
    }

    return response.blob();
  }

  /**
   * The apps this household can import rides from, and where each one stands.
   *
   * Every app the campaign knows comes back, configured or not and connected or
   * not, so the picker can say why one is unavailable rather than leaving a
   * participant hunting for an app that was quietly omitted.
   *
   * @param sessionToken - Bearer token from session
   * @returns Promise resolving to one status per app, in the picker's order
   */
  static async listImportProvidersAsync(sessionToken: string): Promise<ITrackerProviderStatus[]> {
    const response = await this.fetchWithAuthAsync<{ providers: ITrackerProviderStatus[] }>(
      `${CHARITY_API_BASE_PATH}/import/providers`,
      sessionToken
    );
    return Array.isArray(response?.providers) ? response.providers : [];
  }

  /**
   * Start connecting a tracking app.
   *
   * Returns the app's own consent URL rather than redirecting, because the
   * caller is a fetch from a dialog: a redirect on an XHR is followed
   * invisibly instead of moving the page.
   *
   * @param sessionToken - Bearer token from session
   * @param provider - The app being connected
   * @returns Promise resolving to the URL to send the browser to
   * @throws BackendError when the app is not switched on for this campaign
   */
  static async startImportConnectionAsync(
    sessionToken: string,
    provider: TrackerProvider
  ): Promise<{ authorizeUrl: string }> {
    return this.fetchWithAuthAsync<{ authorizeUrl: string }>(
      `${CHARITY_API_BASE_PATH}/import/${provider}/connect`,
      sessionToken,
      { method: "POST" }
    );
  }

  /**
   * Take a household's permission back.
   *
   * Rides already imported stay reported. What goes is our ability to read any
   * more of them.
   *
   * @param sessionToken - Bearer token from session
   * @param provider - The app being disconnected
   * @returns Promise resolving to whether a connection was there to remove
   */
  static async disconnectImportProviderAsync(
    sessionToken: string,
    provider: TrackerProvider
  ): Promise<{ disconnected: boolean }> {
    return this.fetchWithAuthAsync<{ disconnected: boolean }>(
      `${CHARITY_API_BASE_PATH}/import/${provider}/connect`,
      sessionToken,
      { method: "DELETE" }
    );
  }

  /**
   * The rides in a connected app, each marked importable or explained.
   *
   * The duplicate decisions are the service's, never this client's: whether a
   * ride is already reported is decided against the ledger, which the browser
   * cannot see.
   *
   * Who the household has picked goes with the request, because whether a ride
   * may still be imported depends on it: their own second rider's recording of
   * one outing is importable, and the rider who already reported it has nothing
   * left to import. Sending nobody asks the coarser question the service asked
   * before riders were carried, which is the honest answer when nobody is picked.
   *
   * @param sessionToken - Bearer token from session
   * @param provider - Which app to list
   * @param riderIds - Who this import would be for, where the household has picked
   * @returns Promise resolving to the candidates, newest ride first
   * @throws BackendError when the app is no longer connected
   */
  static async listImportCandidatesAsync(
    sessionToken: string,
    provider: TrackerProvider,
    riderIds: string[] = []
  ): Promise<IImportCandidate[]> {
    const query = riderIds
      .map((riderId) => `riderIds=${encodeURIComponent(riderId)}`)
      .join("&");
    const response = await this.fetchWithAuthAsync<{ candidates: IImportCandidate[] }>(
      `${CHARITY_API_BASE_PATH}/import/${provider}/activities${query === "" ? "" : `?${query}`}`,
      sessionToken
    );
    return Array.isArray(response?.candidates) ? response.candidates : [];
  }

  /**
   * Report the rides a household picked.
   *
   * Distances and dates are deliberately not sent. They are read from the app
   * server-side at the moment of the import — a client that could name them
   * could name any figure it liked against a ride it never took.
   *
   * Output shape: `{ imported: [{ providerActivityId, submissionGroupId }],
   * skipped: [{ providerActivityId, rejection, candidate }], reports: [...] }`.
   *
   * @param sessionToken - Bearer token from session
   * @param provider - Which app the rides came from
   * @param providerActivityIds - The rides picked, as the app ids them
   * @param riderIds - Who rode. The same riders are recorded against every ride.
   * @returns Promise resolving to what was recorded and what was not
   */
  static async importRidesAsync(
    sessionToken: string,
    provider: TrackerProvider,
    providerActivityIds: string[],
    riderIds: string[]
  ): Promise<IImportOutcome> {
    return this.fetchWithAuthAsync<IImportOutcome>(
      `${CHARITY_API_BASE_PATH}/import/${provider}/activities`,
      sessionToken,
      {
        method: "POST",
        body: JSON.stringify({ providerActivityIds: providerActivityIds, riderIds: riderIds }),
      }
    );
  }

  /**
   * Renames the household board onto the product's own type.
   *
   * @param params.entries - Households as the endpoint returned them
   * @returns Households as the campaign page and the boards read them
   */
  private static mapLeaderboardHouseholds({
    entries,
  }: {
    entries: LeaderboardHouseholdApiEntry[];
  }): LeaderboardHousehold[] {
    return entries.map((entry) => ({
      rank: entry.rank,
      joinNumber: entry.joinNumber,
      displayName: entry.householdDisplayName,
      miles: entry.milesReported,
      milesToday: entry.milesToday,
      minorRideCount: entry.minorRideCount,
    }));
  }

  /**
   * Renames the adult board onto the product's own type.
   *
   * @param params.entries - Adults as the endpoint returned them
   * @returns Adults as the boards read them
   */
  private static mapLeaderboardAdults({ entries }: { entries: LeaderboardAdultApiEntry[] }): LeaderboardAdult[] {
    return entries.map((entry) => ({
      rank: entry.rank,
      displayName: entry.riderDisplayName,
      miles: entry.milesReported,
    }));
  }

  /**
   * Renames the minor board onto the product's own type.
   *
   * @param params.entries - Minors as the endpoint returned them
   * @returns Minors as the boards read them
   */
  private static mapLeaderboardMinors({ entries }: { entries: LeaderboardMinorApiEntry[] }): LeaderboardMinor[] {
    return entries.map((entry) => ({
      rank: entry.rank,
      displayName: entry.riderDisplayName,
      rideCount: entry.rideCount,
    }));
  }

  /**
   * Returns the backend base URL for use in non-JSON requests.
   */
  private static getBackendBaseUrl(): string {
    return REACT_APP_BACKEND_BASE_URL;
  }
}
